import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, Smartphone, Plus } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";
import HeroShield from "@/components/HeroShield";
import Marquee from "@/components/Marquee";
import ServiceCard from "@/components/ServiceCard";
import CTABand from "@/components/CTABand";
import { Reveal, SectionHead, ease } from "@/components/Reveal";
import {
  COMPANY,
  CYBERSECURITY_SERVICES,
  MOBILE_PRIVACY_SERVICES,
  WHY_POINTS,
  WHY_INTRO,
  WHY_COMMITMENT,
  MARQUEE_ITEMS,
  IMAGES,
} from "@/data/content";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.25 + i * 0.14, ease } }),
};

function Hero() {
  const ref = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    setIsMobile(window.innerWidth < 640);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  // Mouse parallax — slow, spring-smoothed depth movement.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const springCfg = { stiffness: 50, damping: 20, mass: 0.6 };
  const px = useSpring(mx, springCfg);
  const py = useSpring(my, springCfg);
  const shieldX = useTransform(px, (v) => v * 22);
  const shieldY = useTransform(py, (v) => v * 16);
  const bgX = useTransform(px, (v) => v * -12);
  const bgY = useTransform(py, (v) => v * -8);
  const cardsX = useTransform(px, (v) => v * 36);
  const cardsY = useTransform(py, (v) => v * 24);

  const onMouseMove = (e) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-svh flex-col overflow-hidden bg-midnight lg:min-h-screen"
      data-testid="hero-section"
    >
      {/* Layer 1 — cinematic gradient base */}
      <div className="hero-cinematic absolute inset-0" aria-hidden="true" />

      {/* Layer 2 — subtle perspective digital grid */}
      <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-0" aria-hidden="true">
        <div className="hero-grid absolute inset-x-[-12%] bottom-[-18%] h-[65%]" />
      </motion.div>

      {/* Layer 3 — network nodes, biased to the right, clean behind headline */}
      <motion.div style={{ x: bgX, y: bgY }} className="hero-net-mask absolute inset-0" aria-hidden="true">
        <NetworkCanvas className="h-full w-full" density={isMobile ? 32000 : 14000} />
      </motion.div>

      {/* Layer 4 — light trails */}
      <div className="hero-trail top-[30%]" aria-hidden="true" />
      <div className="hero-trail hero-trail-b top-[63%]" aria-hidden="true" />

      {/* Atmospheric glow */}
      <div
        className="blob-pulse pointer-events-none absolute -left-32 top-1/3 h-80 w-80 rounded-full bg-azure/10 blur-[130px]"
        aria-hidden="true"
      />
      <div
        className="blob-pulse pointer-events-none absolute -right-24 top-0 h-96 w-96 rounded-full bg-iris/15 blur-[150px]"
        style={{ animationDelay: "-4s" }}
        aria-hidden="true"
      />
      <div
        className="blob-pulse pointer-events-none absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-aqua/10 blur-[120px]"
        style={{ animationDelay: "-2s" }}
        aria-hidden="true"
      />

      {/* Content — text left, security core right */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 items-center px-6 pb-10 pt-28 lg:px-8 lg:pt-20"
      >
        <div className="grid w-full items-center gap-12 lg:grid-cols-2 lg:gap-6">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease }}
              className="flex items-center gap-4"
            >
              <span className="h-px w-12 bg-gradient-to-r from-iris to-aqua" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-aqua" data-testid="hero-eyebrow">
                {COMPANY.legalName}
              </span>
            </motion.div>

            <h1 className="mt-8 font-display text-[13vw] font-extrabold leading-[0.98] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" variants={lineVariants} custom={0} initial="hidden" animate="show">
                  SECURING THE
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span
                  className="text-gradient-hero block"
                  variants={lineVariants}
                  custom={1}
                  initial="hidden"
                  animate="show"
                >
                  DIGITAL FUTURE
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              data-testid="hero-tagline"
            >
              {COMPANY.tagline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact-us"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-iris to-azure px-8 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(108,59,255,0.45)]"
              >
                Contact Us
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/service"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 rounded-full border border-iris/40 bg-transparent px-8 py-4 font-display text-sm font-bold text-white/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-aqua hover:text-aqua hover:shadow-[0_0_32px_rgba(32,199,201,0.22)]"
              >
                Explore Our Services
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          <motion.div style={{ x: shieldX, y: shieldY }} className="flex justify-center lg:justify-end">
            <HeroShield reduced={reduced} cx={cardsX} cy={cardsY} />
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom information strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="relative z-10 border-t border-white/10 bg-midnight/40 backdrop-blur-sm"
      >
        <div
          className="mx-auto flex w-full max-w-7xl items-center justify-between gap-6 px-6 py-5 lg:px-8"
          data-testid="hero-meta-row"
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            {["Incorporated 2016", "Cyber Security", "Mobile Privacy Protection"].map((item, i, arr) => (
              <span key={item} className="flex items-center gap-8">
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/45">{item}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rotate-45 bg-aqua/60" aria-hidden="true" />}
              </span>
            ))}
          </div>
          <ChevronDown className="hidden h-5 w-5 animate-scroll-cue text-white/40 lg:block" aria-hidden="true" />
        </div>
      </motion.div>
    </section>
  );
}

function CompanySection() {
  return (
    <section className="bg-offwhite" data-testid="company-intro-section">
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-6 py-24 lg:grid-cols-12 lg:gap-12 lg:px-8 lg:py-32">
        <div className="lg:col-span-4">
          <SectionHead num="01" eyebrow="The Company" />
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <h3 className="max-w-2xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Building a More <span className="text-gradient-light">Secure Digital Future</span>
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink" data-testid="company-incorporation">
              {COMPANY.incorporation}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-4 max-w-2xl text-lg leading-relaxed text-steel" data-testid="company-positioning">
              {COMPANY.tagline}
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <Link
              to="/about"
              data-testid="company-about-link"
              className="group mt-10 inline-flex items-center gap-2 border-b-2 border-iris pb-1 font-display text-sm font-bold text-iris transition-colors duration-300 hover:border-aqua hover:text-navy"
            >
              MORE ABOUT PROTECH
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="bg-white" data-testid="home-services-section">
      <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHead
            num="02"
            eyebrow="Core Cybersecurity Services"
            title="Hardening your systems, from configuration to network."
          />
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="home-services-grid">
          {CYBERSECURITY_SERVICES.map((service, i) => (
            <Reveal key={service.num} delay={i * 0.1} className="h-full">
              <ServiceCard service={service} to="/service" testIdPrefix="home-service-card" />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <a
            href="#technology"
            data-testid="home-mobile-privacy-banner"
            className="group mt-8 flex flex-col justify-between gap-6 rounded-2xl border border-hairline bg-offwhite p-8 transition-[border-color,box-shadow] duration-300 hover:border-iris hover:shadow-[0_24px_50px_rgba(108,59,255,0.12)] md:flex-row md:items-center"
          >
            <div className="flex items-start gap-6">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-iris text-white transition-colors duration-300 group-hover:bg-aqua group-hover:text-midnight">
                <Smartphone className="h-5 w-5" />
              </span>
              <div>
                <span className="font-display text-xs font-extrabold tracking-widest text-steel/70">/04 — /06</span>
                <h3 className="mt-1 font-display text-xl font-bold tracking-tight text-navy lg:text-2xl">
                  Mobile Privacy Protection
                </h3>
                <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-steel">
                  Cutting-edge cybersecurity solutions tailored for portable devices, including Android and iOS
                  platforms — cloud systems, auto response and AES encryption.
                </p>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 font-display text-sm font-bold text-iris transition-colors duration-300 group-hover:text-navy">
              EXPLORE TECHNOLOGY
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function TechnologySection() {
  const [open, setOpen] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section
      id="technology"
      ref={ref}
      className="noise relative overflow-hidden bg-gradient-to-b from-midnight to-navy-light"
      data-testid="home-technology-section"
    >
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-iris/15 blur-[150px]" />
      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:gap-20 lg:px-8 lg:py-32">
        <div className="relative order-2 lg:order-1">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.45)]">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.mobile}
                alt="Abstract visualization of encrypted mobile data protection"
                className="h-[420px] w-full scale-110 object-cover lg:h-[520px]"
                loading="lazy"
                data-testid="home-technology-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/70 via-transparent to-transparent" />
            </div>
          </Reveal>
          <div
            className="absolute -bottom-5 -right-2 rounded-2xl border border-white/10 bg-navy/90 px-6 py-4 shadow-xl backdrop-blur-md sm:right-8"
            data-testid="home-technology-chip"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-aqua">Platform</p>
            <p className="mt-1 font-display text-sm font-bold text-white">Android &amp; iOS Protection</p>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <SectionHead num="03" eyebrow="Technology & Solutions" title="Mobile Privacy Protection" dark />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
              Our mission is to empower individuals and organizations by providing cutting-edge cybersecurity solutions
              tailored for portable devices, including Android and iOS platforms.
            </p>
          </Reveal>
          <div className="mt-10" data-testid="home-technology-accordion">
            {MOBILE_PRIVACY_SERVICES.map((service, i) => (
              <div key={service.num} className="border-b border-white/10">
                <button
                  type="button"
                  data-testid={`tech-accordion-${service.num}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="group flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="flex items-center gap-5">
                    <span className="font-display text-sm font-extrabold text-aqua">{service.num}</span>
                    <span
                      className={`font-display text-lg font-bold transition-colors duration-300 ${
                        open === i ? "text-aqua" : "text-white group-hover:text-aqua"
                      }`}
                    >
                      {service.name}
                    </span>
                  </span>
                  <Plus
                    className={`h-4 w-4 shrink-0 text-aqua transition-transform duration-300 ${
                      open === i ? "rotate-45" : ""
                    }`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 pl-10 pr-4 text-[15px] leading-relaxed text-slate-300">{service.desc}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="bg-offwhite" data-testid="home-why-section">
      <div className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <SectionHead num="04" eyebrow="Why Protech" title="Ready to safeguard your digital future." />
        <div
          className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4"
          data-testid="home-why-grid"
        >
          {WHY_POINTS.map((point, i) => (
            <Reveal key={point.num} delay={i * 0.08} className="h-full">
              <div className="group flex h-full flex-col bg-white p-8 transition-colors duration-300 hover:bg-white">
                <span className="font-display text-4xl font-extrabold text-hairline transition-colors duration-300 group-hover:text-gradient">
                  {point.num}
                </span>
                <h3 className="mt-8 font-display text-lg font-bold tracking-tight text-navy">{point.title}</h3>
                <span className="mt-auto block pt-8">
                  <span className="block h-0.5 w-8 bg-gradient-to-r from-iris to-aqua transition-all duration-300 group-hover:w-full" />
                </span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p
            className="mt-12 max-w-3xl font-display text-xl font-semibold leading-relaxed text-navy lg:text-2xl"
            data-testid="home-why-commitment"
          >
            “{WHY_COMMITMENT}”
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function StorytellingSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section ref={ref} className="noise relative overflow-hidden bg-navy" data-testid="storytelling-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.shield}
          alt="Abstract digital security shield formed by glowing network nodes"
          className="h-full w-full scale-110 object-cover opacity-35"
          loading="lazy"
          data-testid="storytelling-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-midnight via-midnight/40 to-midnight" />
      <div className="relative mx-auto w-full max-w-7xl px-6 py-32 lg:px-8 lg:py-44">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-gradient-to-r from-iris to-aqua" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-aqua">{COMPANY.shortName}</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2
            className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
            data-testid="storytelling-headline"
          >
            Security for a <span className="text-gradient">Connected World.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="storytelling-tagline">
            {COMPANY.tagline}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <CompanySection />
      <ServicesSection />
      <TechnologySection />
      <WhySection />
      <StorytellingSection />
      <CTABand />
    </main>
  );
}
